import { addDays, format, startOfMonth, subMonths } from "date-fns";
import type { NestedDeal } from "@revelate/types";
import { getGrowthEfficiencyCreatedDate, getGrowthEfficiencyProperties } from "../state-of-sales-v2/growthEfficiencyData";
import type { CreationExploration } from "./creationExploration";
import type { CreationMarketField } from "./creationDimensionData";

export type CreationProductRead = { companyId: number; labels: Readonly<Record<string, readonly string[]>>; markets?: Readonly<Record<string, string>>; marketField?: string };

const sourceDeal = (deal: NestedDeal) => ({ ...deal, emails: undefined, meetings: undefined, activity_sync_status: undefined });

// TODO(Emil/Stefan/Zabir): remove the live-label supplement once the ordinary sync
// saves deal_products. This approved temporary read must never import CRM amounts,
// persist anything, or change the Forecast/shared provider contract.
export function creationProductRequest(companyId: number | undefined, rawDeals: NestedDeal[], asOf: Date, market?: CreationMarketField) {
  if (companyId !== 53) return null;
  const firstMonth = startOfMonth(subMonths(asOf, 12));
  const deals = rawDeals.filter(deal => {
    const created = getGrowthEfficiencyCreatedDate(sourceDeal(deal));
    return created && created >= firstMonth && created < asOf && deal.provider === "hubspot" && deal.provider_id;
  });
  const missing = deals.filter(deal => {
    const properties = getGrowthEfficiencyProperties(sourceDeal(deal));
    return !Object.hasOwn(properties, "deal_products") || (market && !Object.hasOwn(properties, market.name));
  });
  if (!missing.length) return null;
  // The existing reader filters close date, not creation date. Cover the retained
  // deals' recorded close dates, with margins for its strict GT/LT comparisons.
  const closes = missing.flatMap(deal => {
    const value = deal.closed_at ? new Date(deal.closed_at).getTime() : NaN;
    return Number.isFinite(value) ? [value] : [];
  });
  const from = format(addDays(new Date(Math.min(firstMonth.getTime(), ...closes)), -1), "yyyy-MM-dd");
  const to = format(addDays(new Date(Math.max(asOf.getTime(), ...closes)), 1), "yyyy-MM-dd");
  return { companyId, from, to, providerIds: missing.map(deal => String(deal.provider_id)), market };
}

export async function readCreationProducts(request: NonNullable<ReturnType<typeof creationProductRequest>>, read: (path: string, cache?: boolean) => Promise<unknown>): Promise<CreationProductRead> {
  if (request.companyId !== 53) throw new Error("Product-label scope is not approved for this company.");
  const query = new URLSearchParams({ from: request.from, to: request.to, properties: "deal_products" });
  if (request.market) query.append("properties", request.market.name);
  const result = await read(`providers/hubspot/view/deals/${request.companyId}?${query}`, false);
  if (!result || typeof result !== "object" || !("results" in result) || !Array.isArray(result.results)
    || !("count" in result) || result.count !== result.results.length) throw new Error("CRM product labels could not be read.");
  const wanted = new Set(request.providerIds);
  const labels: Record<string, string[]> = {};
  const markets: Record<string, string> = {};
  for (const row of result.results as unknown[]) {
    if (!row || typeof row !== "object" || !("id" in row) || !wanted.has(String(row.id))) continue;
    const properties = "properties" in row ? row.properties : null;
    const value = properties && typeof properties === "object" && "deal_products" in properties ? properties.deal_products : undefined;
    if (typeof value === "string" || value === null) labels[String(row.id)] = [...new Set<string>((value ?? "").split(";").map(label => label.trim()).filter(Boolean))];
    const market = request.market && properties && typeof properties === "object" ? (properties as Record<string, unknown>)[request.market.name] : undefined;
    if (typeof market === "string" && market.trim()) markets[String(row.id)] = request.market?.options[market] ?? market.trim();
  }
  return { companyId: request.companyId, labels, ...(request.market ? { markets, marketField: request.market.name } : {}) };
}

export function withCreationProducts(source: CreationExploration, companyId: number | undefined, rawDeals: NestedDeal[], products: CreationProductRead | undefined, status?: "loading" | "error"): CreationExploration {
  if (companyId !== 53 || (products && products.companyId !== companyId)) return source;
  const byId = new Map(rawDeals.map(deal => [String(deal.id), deal]));
  return { ...source, productLabelsStatus: status ?? (products ? "live" : undefined), observations: source.observations.map(row => {
    const raw = byId.get(row.id);
    if (!raw || raw.provider !== "hubspot") return row;
    const properties = getGrowthEfficiencyProperties(sourceDeal(raw));
    const labels = !Object.hasOwn(properties, "deal_products") ? products?.labels[String(raw.provider_id)] : undefined;
    const market = products?.marketField && !Object.hasOwn(properties, products.marketField) ? products.markets?.[String(raw.provider_id)] : undefined;
    return labels || market ? { ...row, ...(labels ? { products: labels } : {}), ...(market ? { dimensions: { ...row.dimensions, market } } : {}) } : row;
  }) };
}
